
export default function Loader({
  size = 12,
  color = "#414141ff",
  type = "ring",
  className = ""
}) {
  const loaderStyle = {
    display: "inline-flex",
    justifyContent: "center",
    alignItems: "center",
  };

  // Spinner with gradient effect
  const spinnerStyle = {
    width: size,
    height: size,
    border: `${size / 8}px solid ${color}20`,
    borderTop: `${size / 8}px solid ${color}`,
    borderRight: `${size / 8}px solid ${color}`,
    borderLeft: `${size / 8}px solid ${color}`,
    borderRadius: "50%",
    animation: "spin 1.2s cubic-bezier(0.5, 0.1, 0.5, 0.9) infinite",
  };

  // Modern dots with wave effect
  const dotStyle = {
    display: "flex",
    gap: size / 3,
  };

  const dotItem = {
    width: size / 2,
    height: size / 2,
    backgroundColor: color,
    borderRadius: "50%",
    animation: "wave 1.8s ease-in-out infinite",
  };

  // Pulse with gradient
  const pulseStyle = {
    width: size,
    height: size,
    backgroundColor: color,
    borderRadius: "50%",
    animation: "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
    boxShadow: `0 0 0 0 ${color}40`,
  };

  // New: Ring loader
  const ringStyle = {
    width: size,
    height: size,
    border: `${size / 8}px solid transparent`,
    borderTop: `${size / 8}px solid ${color}`,
    borderRadius: "50%",
    animation: "spin 1.5s linear infinite",
    position: "relative",
  };

  const ringInner = {
    content: '""',
    position: "absolute",
    top: `${-size / 8}px`,
    left: `${-size / 8}px`,
    right: `${-size / 8}px`,
    bottom: `${-size / 8}px`,
    border: `${size / 8}px solid transparent`,
    borderTop: `${size / 8}px solid ${color}`,
    borderRadius: "50%",
    animation: "spin 1s linear infinite reverse",
    opacity: 0.7,
  };

  // New: Fast spinning dotted ring
  // Premium Fast Dotted Spinner
  const premiumFastDottedStyle = {
    width: size,
    height: size,
    borderRadius: "50%",
    position: "relative",
    animation: "premiumSpin 0.7s linear infinite",
    background: `conic-gradient(
    from 0deg,
    ${color} 0deg 10deg,
    transparent 10deg 40deg,
    ${color} 40deg 50deg,
    transparent 50deg 80deg,
    ${color} 80deg 90deg,
    transparent 90deg 120deg,
    ${color} 120deg 130deg,
    transparent 130deg 160deg,
    ${color} 160deg 170deg,
    transparent 170deg 200deg,
    ${color} 200deg 210deg,
    transparent 210deg 240deg,
    ${color} 240deg 250deg,
    transparent 250deg 280deg,
    ${color} 280deg 290deg,
    transparent 290deg 320deg,
    ${color} 320deg 330deg,
    transparent 330deg 360deg
  )`,
    mask: "radial-gradient(farthest-side, transparent calc(100% - 4px), black 0)",
    WebkitMask:
      "radial-gradient(farthest-side, transparent calc(100% - 4px), black 0)",
    boxShadow: `0 0 ${size / 2}px ${color}40`,
  };
  // New: Skeleton wave
  const skeletonStyle = {
    width: size * 3,
    height: size,
    background: `linear-gradient(90deg, ${color}20 25%, ${color}40 50%, ${color}20 75%)`,
    backgroundSize: "200% 100%",
    borderRadius: size / 4,
    animation: "skeletonWave 2s infinite",
  };

  // New: Bouncing bars
  const barsStyle = {
    display: "flex",
    alignItems: "flex-end",
    gap: size / 5,
    height: size,
  };

  const barItem = {
    width: size / 4,
    backgroundColor: color,
    borderRadius: size / 8,
    animation: "bars 1.4s ease-in-out infinite",
  };

  return (
    <div style={loaderStyle} className={className}>
      {type === "spinner" && <div style={spinnerStyle} />}
{type === "premium-fast-dotted" && (
  <div style={premiumFastDottedStyle} />
)}
      {type === "dots" && (
        <div style={dotStyle}>
          <div style={{ ...dotItem, animationDelay: "0s" }} />
          <div style={{ ...dotItem, animationDelay: "0.3s" }} />
          <div style={{ ...dotItem, animationDelay: "0.6s" }} />
        </div>
      )}

      {type === "pulse" && <div style={pulseStyle} />}

      {type === "ring" && (
        <div style={ringStyle}>
          <div style={ringInner} />
        </div>
      )}

      {type === "skeleton" && <div style={skeletonStyle} />}

      {type === "bars" && (
        <div style={barsStyle}>
          <div style={{ ...barItem, height: size * 0.3, animationDelay: "0s" }} />
          <div style={{ ...barItem, height: size * 0.6, animationDelay: "0.1s" }} />
          <div style={{ ...barItem, height: size, animationDelay: "0.2s" }} />
          <div style={{ ...barItem, height: size * 0.6, animationDelay: "0.3s" }} />
          <div style={{ ...barItem, height: size * 0.3, animationDelay: "0.4s" }} />
        </div>
      )}

      <style jsx>{`
        @keyframes premiumSpin {
  100% { transform: rotate(360deg); }
}
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes wave {
          0%, 60%, 100% { 
            transform: translateY(0);
            opacity: 0.6;
          }
          30% { 
            transform: translateY(-${size / 1.5}px);
            opacity: 1;
          }
        }
        
        @keyframes pulse {
          0% { 
            transform: scale(0.95);
            box-shadow: 0 0 0 0 ${color}80;
          }
          70% { 
            transform: scale(1);
            box-shadow: 0 0 0 ${size / 2}px ${color}00;
          }
          100% { 
            transform: scale(0.95);
            box-shadow: 0 0 0 0 ${color}00;
          }
        }
        
        @keyframes skeletonWave {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        
        @keyframes bars {
          0%, 40%, 100% { 
            transform: scaleY(0.4);
          }
          20% { 
            transform: scaleY(1);
          }
        }
      `}</style>
    </div>
  );
}